import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import * as THREE from 'three';
import OGCWebServiceHelper from "../Provider/OGCWebServiceHelper";
import CRS from "./Geographic/Crs";
/**
 * A TileMesh is a THREE.Mesh with a geometricError and an OBB
 * The objectId property of the material is the with the id of the TileMesh
 * @constructor
 * @param {TileGeometry} geometry - the tile geometry
 * @param {THREE.Material} material - a THREE.Material compatible with THREE.Mesh
 * @param {Layer} layer - the layer the tile is added to
 * @param {Extent} extent - the tile extent
 * @param {?number} level - the tile level (default = 0)
 */

var TileMesh =
/*#__PURE__*/
function (_THREE$Mesh) {
  _inherits(TileMesh, _THREE$Mesh);

  function TileMesh(geometry, material, layer, extent) {
    var _this;

    var level = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;

    _classCallCheck(this, TileMesh);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TileMesh).call(this, geometry, material));

    if (!extent) {
      throw new Error('extent is mandatory to build a TileMesh');
    }

    _this.layer = layer;
    _this.extent = extent;
    _this.level = level; // Set equivalent tiled extent zoom
    // Is used to set zoom for each texture fetched with no tiled extent
    // It's more simple to set zoom here instead of reverse ingeneer
    // Removable with a table pixel/extent.size

    if (!_this.extent.zoom) {
      _this.extent.zoom = level;
    }

    _this.material.objectId = _this.id;
    _this.obb = _this.geometry.OBB.clone();
    _this.boundingSphere = new THREE.Sphere();

    _this.obb.box3D.getBoundingSphere(_this.boundingSphere);

    _this.wmtsCoords = {};
    _this.frustumCulled = false;
    _this.matrixAutoUpdate = false;
    _this.rotationAutoUpdate = false;
    _this.layerUpdateState = {};
    _this.isTileMesh = true;
    return _this;
  }
  /**
   * If specified, update the min and max elevation of the OBB
   * and updates accordingly the bounding sphere and the geometric error
   *
   * @param {?number} min
   * @param {?number} max
   */


  _createClass(TileMesh, [{
    key: "setBBoxZ",
    value: function setBBoxZ(min, max) {
      if (min == undefined && max == undefined) {
        return;
      } // FIXME: Why the floors ? This is not conservative : the obb may be too short by almost 1m !


      if (Math.floor(min) !== Math.floor(this.obb.z.min) || Math.floor(max) !== Math.floor(this.obb.z.max)) {
        this.obb.updateZ(min, max);
        this.obb.box3D.getBoundingSphere(this.boundingSphere);
      }
    }
  }, {
    key: "getCoordsForSource",
    value: function getCoordsForSource(source) {
      if (source.isWMTSSource) {
        OGCWebServiceHelper.computeTileMatrixSetCoordinates(this, source.tileMatrixSet);
        return this.wmtsCoords[source.tileMatrixSet];
      } else if (source.isWMSSource && this.extent.crs != source.projection) {
        if (source.projection == 'EPSG:3857') {
          var tilematrixset = 'PM';
          OGCWebServiceHelper.computeTileMatrixSetCoordinates(this, tilematrixset);
          return this.wmtsCoords[tilematrixset];
        } else {
          throw new Error('unsupported projection wms for this viewer');
        }
      } else if (source.isTMSSource) {
        // Special globe case: use the P(seudo)M(ercator) coordinates
        if (CRS.is4326(this.extent.crs) && (source.extent.crs == 'EPSG:3857' || CRS.is4326(source.extent.crs))) {
          OGCWebServiceHelper.computeTileMatrixSetCoordinates(this, 'PM');
          return this.wmtsCoords.PM;
        } else {
          return OGCWebServiceHelper.computeTMSCoordinates(this, source.extent, source.isInverted);
        }
      } else if (source.extent.crs == this.extent.crs) {
        // Currently extent.as() always clone the extent, even if the output
        // crs is the same.
        // So we avoid using it if both crs are the same.
        return [this.extent];
      } else {
        return [this.extent.as(source.extent.crs)];
      }
    }
  }, {
    key: "getZoomForLayer",
    value: function getZoomForLayer(layer) {
      return this.getCoordsForSource(layer.source)[0].zoom || this.level;
    }
    /**
     * Search for a common ancestor between this tile and another one. It goes
     * through parents on each side until one is found.
     *
     * @param {TileMesh} tile
     *
     * @return {TileMesh} the resulting common ancestor
     */

  }, {
    key: "findCommonAncestor",
    value: function findCommonAncestor(tile) {
      if (!tile) {
        return undefined;
      }

      if (tile.level == this.level) {
        if (tile.id == this.id) {
          return tile;
        } else if (tile.level != 0) {
          return this.parent.findCommonAncestor(tile.parent);
        } else {
          return undefined;
        }
      } else if (tile.level < this.level) {
        return this.parent.findCommonAncestor(tile);
      } else {
        return this.findCommonAncestor(tile.parent);
      }
    }
  }, {
    key: "findAncestorFromLevel",
    value: function findAncestorFromLevel(targetLevel) {
      var parentAtLevel = this;

      while (parentAtLevel && parentAtLevel.level > targetLevel) {
        parentAtLevel = parentAtLevel.parent;
      }

      if (!parentAtLevel) {
        return Promise.reject("Invalid targetLevel requested ".concat(targetLevel));
      }

      return parentAtLevel;
    }
  }, {
    key: "onBeforeRender",
    value: function onBeforeRender() {
      if (this.material.layersNeedUpdate) {
        this.material.updateLayersUniforms();
      }
    }
  }]);

  return TileMesh;
}(THREE.Mesh);

export default TileMesh;